'use memo'

import { useEffect, useState } from 'react'
import { AgxCurrency } from '@urbanx/agx-ui-components'
import { AgxRadioGroup, AgxSpacer } from '@urbanx/agx.ui-components2'
import {
  StaffGetSaleInformationResponseSaleMethod,
  StaffGetSaleInformationResponseUnconditionalDisplayAs,
  StaffGetSaleInformationResponseUnderOfferDisplayAs,
  StaffUpdateSaleInformationRequest,
  StaffUpdateSaleInformationRequestSaleMethod,
  StaffUpdateSaleInformationRequestUnconditionalDisplayAs,
  StaffUpdateSaleInformationRequestUnderOfferDisplayAs,
  useInvalidateUseStaffGetSaleInformation,
  useStaffGetSaleInformation,
  useStaffUpdateSaleInformation,
  useInvalidateUseStaffGetContractDeposits,
  useInvalidateUseStaffGetContract,
} from '@urbanx/agx.campaigns.client'
import { useSelectedContract } from 'hooks'
import { MenuTitles, Pages } from 'constants/menu'
import { ContractSubPage } from '../ContractSubPage'
import { ErrorModal } from 'helpers/ErrorModal'
import { getContractInformationSubPages } from './ContractInformationSubPages'
import { SubPagePanel } from 'components/panel/SubPagePanel'
import LoadingState from 'components/loadingState/LoadingState'
import { RexDisconnectedModalFields } from 'pages/campaigns/components/manageCampaign/RexDataUpdateHelper'
import { useGetListingByCampaignId } from '@urbanx/agx.rex.client'
import { isEqual } from 'lodash-es'
import {
  ContractSettingsUnconditionalDisplayAs,
  ContractSettingsUnderOfferDisplayAs,
} from '@urbanx/agx.agencies.client'
import useRexDisconnectedModal from 'pages/campaigns/components/manageCampaign/useRexDisconnectedModal'
import TerminateContractAction from '../TerminateContractAction'

function toRequestUnderOfferDisplayAs(
  value: StaffGetSaleInformationResponseUnderOfferDisplayAs | undefined
): StaffUpdateSaleInformationRequestUnderOfferDisplayAs | undefined {
  switch (value) {
    case StaffGetSaleInformationResponseUnderOfferDisplayAs.Available:
      return StaffUpdateSaleInformationRequestUnderOfferDisplayAs.Available
    case StaffGetSaleInformationResponseUnderOfferDisplayAs.UnderOffer:
      return StaffUpdateSaleInformationRequestUnderOfferDisplayAs.UnderOffer
    default:
      return undefined
  }
}

function toRequestUnconditionalDisplayAs(
  value: StaffGetSaleInformationResponseUnconditionalDisplayAs | undefined
): StaffUpdateSaleInformationRequestUnconditionalDisplayAs | undefined {
  switch (value) {
    case StaffGetSaleInformationResponseUnconditionalDisplayAs.Sold:
      return StaffUpdateSaleInformationRequestUnconditionalDisplayAs.Sold
    case StaffGetSaleInformationResponseUnconditionalDisplayAs.UnderOffer:
      return StaffUpdateSaleInformationRequestUnconditionalDisplayAs.UnderOffer
    default:
      return undefined
  }
}

const SaleInformation = () => {
  const { contract, campaignId } = useSelectedContract()
  const [isLoaded, setIsLoaded] = useState(false)
  const [validate, setValidate] = useState(false)
  const [openErrorModal, setOpenErrorModal] = useState(false)
  const [submitFormErrors, setSubmitFormErrors] = useState<string[]>([])
  const invalidateUseStaffGetSaleInformation =
    useInvalidateUseStaffGetSaleInformation()
  const invalidateUseStaffGetContractDeposits =
    useInvalidateUseStaffGetContractDeposits()
  const invalidateUseStaffGetContract = useInvalidateUseStaffGetContract()

  const {
    data: saleInformation,
    isPending,
    isError,
  } = useStaffGetSaleInformation(
    campaignId || '',
    contract?.contractId || '',
    !!campaignId && !!contract?.contractId
  )

  const [salePrice, setSalePrice] = useState<number | undefined>(
    saleInformation?.salePrice
  )
  const [displaySalePrice, setDisplaySalePrice] = useState<boolean | undefined>(
    saleInformation?.displaySalePrice
  )
  const [saleMethod, setSaleMethod] = useState<
    StaffGetSaleInformationResponseSaleMethod | undefined
  >(saleInformation?.saleMethod)
  const { modal: rexDisconnectedModal, openWithChanges } =
    useRexDisconnectedModal()
  const [underOfferDisplayAs, setUnderOfferDisplayAs] = useState<
    StaffGetSaleInformationResponseUnderOfferDisplayAs | undefined
  >(saleInformation?.underOfferDisplayAs)
  const [unconditionalDisplayAs, setUnconditionalDisplayAs] = useState<
    StaffGetSaleInformationResponseUnconditionalDisplayAs | undefined
  >(saleInformation?.unconditionalDisplayAs)

  useEffect(() => {
    if (!saleInformation) return
    setSalePrice(saleInformation?.salePrice ?? undefined)
    setDisplaySalePrice(saleInformation?.displaySalePrice ?? false)
    setSaleMethod(
      saleInformation?.saleMethod ??
        StaffGetSaleInformationResponseSaleMethod.PrivateTreaty
    )
    setUnderOfferDisplayAs(saleInformation?.underOfferDisplayAs)
    setUnconditionalDisplayAs(saleInformation?.unconditionalDisplayAs)
    setIsLoaded(!isPending)
  }, [saleInformation, isPending])

  useEffect(() => {
    setValidate(
      saleInformation?.salePrice != salePrice ||
        saleInformation?.displaySalePrice !== displaySalePrice ||
        saleInformation?.saleMethod !== saleMethod ||
        saleInformation?.underOfferDisplayAs !== underOfferDisplayAs ||
        saleInformation?.unconditionalDisplayAs !== unconditionalDisplayAs
    )
  }, [
    salePrice,
    displaySalePrice,
    saleMethod,
    underOfferDisplayAs,
    unconditionalDisplayAs,
  ])

  const { staffUpdateSaleInformation, staffUpdateSaleInformationPending } =
    useStaffUpdateSaleInformation({
      onSuccess: () => {},
      onError: () => {},
    })

  const { data: rexListing, isPending: isPendingRexListing } =
    useGetListingByCampaignId(campaignId || '', campaignId !== undefined)

  const normalisedSalePrice = saleInformation?.salePrice ?? undefined
  const normalisedDisplaySalePrice = saleInformation?.displaySalePrice ?? false
  const normalisedSaleMethod =
    saleInformation?.saleMethod ??
    StaffGetSaleInformationResponseSaleMethod.PrivateTreaty
  const normalisedUnderOfferDisplayAs = saleInformation?.underOfferDisplayAs
  const normalisedUnconditionalDisplayAs =
    saleInformation?.unconditionalDisplayAs

  const isFormDirty =
    normalisedSalePrice !== salePrice ||
    normalisedDisplaySalePrice !== displaySalePrice ||
    normalisedSaleMethod !== saleMethod ||
    normalisedUnderOfferDisplayAs !== underOfferDisplayAs ||
    normalisedUnconditionalDisplayAs !== unconditionalDisplayAs

  const onSave = async (success: () => void) => {
    if (!campaignId || !contract?.contractId) return

    if (!rexListing && isPendingRexListing === false) {
      const prevSalePrice = saleInformation?.salePrice ?? undefined
      const newSalePrice = salePrice

      openWithChanges(
        !isEqual(prevSalePrice, newSalePrice)
          ? [
              {
                field: RexDisconnectedModalFields.SalePrice,
                value: newSalePrice,
              },
            ]
          : []
      )
    }

    const request: StaffUpdateSaleInformationRequest = {
      displaySalePrice: displaySalePrice,
      salePrice: salePrice,
      underOfferDisplayAs: toRequestUnderOfferDisplayAs(underOfferDisplayAs),
      unconditionalDisplayAs: toRequestUnconditionalDisplayAs(
        unconditionalDisplayAs
      ),
    }

    if (saleMethod === StaffGetSaleInformationResponseSaleMethod.Auction) {
      request.saleMethod = StaffUpdateSaleInformationRequestSaleMethod.Auction
    } else if (
      saleMethod === StaffGetSaleInformationResponseSaleMethod.PrivateTreaty
    ) {
      request.saleMethod =
        StaffUpdateSaleInformationRequestSaleMethod.PrivateTreaty
    }

    await staffUpdateSaleInformation(
      {
        body: request,
        campaignId: campaignId,
        contractId: contract.contractId,
      },
      {
        onSuccess: async (data) => {
          if (data?.success) {
            success()
            await invalidateUseStaffGetSaleInformation(
              campaignId ?? '',
              contract.contractId
            )
            await invalidateUseStaffGetContractDeposits(
              campaignId ?? '',
              contract.contractId
            )
            await invalidateUseStaffGetContract(contract.contractId)
          } else {
            setSubmitFormErrors(data?.errors ?? [])
            setOpenErrorModal(true)
          }
        },
      }
    )
  }

  if (!isLoaded) {
    return <LoadingState />
  }

  return (
    <ContractSubPage
      key={`SaleInformationPage${isPending ? 'Loaded' : ''}`}
      pageTitle="Contract Information"
      secondaryActiveMenu={MenuTitles.CONTRACT_INFORMATION}
      currentPage={Pages.SALE_INFORMATION}
      subPages={getContractInformationSubPages()}
      onSaveChanges={onSave}
      isFormDirty={isFormDirty}
      isSaving={staffUpdateSaleInformationPending}
      isError={isError}
      secondaryActions={<TerminateContractAction />}
    >
      <SubPagePanel
        key={`SaleInformationSalePrice${isPending ? 'Loaded' : ''}`}
        panelTitle="Sale Price"
        expandable
        defaultExpanded
      >
        <AgxCurrency
          key={`saleInformation_SalePrice${isPending ? 'Loaded' : ''}`}
          id="saleInformation_SalePrice"
          required
          onInputValueChange={({ value: updatedValue }) => {
            setSalePrice(
              updatedValue ? parseFloat(updatedValue) || undefined : undefined
            )
          }}
          stretch
          defaultValue={salePrice?.toString() || ''}
          validate={validate}
          decimalPoints={2}
          displayErrors={true}
        />
      </SubPagePanel>
      <SubPagePanel
        key={`SaleInformationSaleMethod${isPending ? 'Loaded' : ''}`}
        panelTitle="Sale Method"
        expandable
        defaultExpanded
      >
        <AgxRadioGroup
          key={`saleInformation_SaleMethod${isPending ? 'Loaded' : ''}`}
          id="saleInformation_SaleMethod"
          layout="stacked"
          options={[
            {
              value: StaffGetSaleInformationResponseSaleMethod.PrivateTreaty,
              label: 'Private Treaty',
            },
            {
              value: StaffGetSaleInformationResponseSaleMethod.Auction,
              label: 'Auction',
            },
          ]}
          onChange={(val) => {
            setSaleMethod(val as StaffGetSaleInformationResponseSaleMethod)
          }}
          value={
            saleMethod ||
            StaffGetSaleInformationResponseSaleMethod.PrivateTreaty
          }
        />
      </SubPagePanel>
      <SubPagePanel
        key={`SaleInformationDisplaySalePrice${isPending ? 'Loaded' : ''}`}
        panelTitle="Display Sale Price"
        expandable
        defaultExpanded
      >
        <AgxRadioGroup
          key={`saleInformation_DisplaySalePrice${isPending ? 'Loaded' : ''}`}
          id="saleInformation_DisplaySalePrice"
          layout="stacked"
          options={[
            {
              value: 'Yes',
              label: 'Yes, display sale price',
            },
            {
              value: 'No',
              label: 'No, hide sale price',
            },
          ]}
          onChange={(val) => {
            setDisplaySalePrice(val === 'Yes')
          }}
          value={displaySalePrice ? 'Yes' : 'No'}
          label="Display sale price once marked as SOLD?"
        />
      </SubPagePanel>
      <SubPagePanel
        key={`SaleInformationRexListingStatusPreference${isPending ? 'Loaded' : ''}`}
        panelTitle="REX Listing Status Preference"
        expandable
        defaultExpanded
      >
        <AgxSpacer gap="md">
          <AgxRadioGroup
            key={`saleInformation_DisplayUnderOffer${isPending ? 'Loaded' : ''}`}
            id="saleInformation_DisplayUnderOffer"
            layout="stacked"
            options={[
              {
                value: ContractSettingsUnderOfferDisplayAs.Available,
                label: 'Available',
              },
              {
                value: ContractSettingsUnderOfferDisplayAs.UnderOffer,
                label: 'Under Offer',
              },
            ]}
            onChange={(val) => {
              setUnderOfferDisplayAs(
                val as StaffGetSaleInformationResponseUnderOfferDisplayAs
              )
            }}
            value={underOfferDisplayAs}
            label="When Contract “Accepted”, set listing status to:"
          />
          <AgxRadioGroup
            key={`saleInformation_DisplayUnconditional${isPending ? 'Loaded' : ''}`}
            id="saleInformation_DisplayUnconditional"
            layout="stacked"
            options={[
              {
                value: ContractSettingsUnconditionalDisplayAs.UnderOffer,
                label: 'Under Offer',
              },
              {
                value: ContractSettingsUnconditionalDisplayAs.Sold,
                label: 'Sold',
              },
            ]}
            onChange={(val) => {
              setUnconditionalDisplayAs(
                val as StaffGetSaleInformationResponseUnconditionalDisplayAs
              )
            }}
            value={unconditionalDisplayAs}
            label="When Contract “Unconditional”, set listing status to:"
          />
        </AgxSpacer>
      </SubPagePanel>
      {openErrorModal && (
        <ErrorModal
          errorList={submitFormErrors}
          setShowModal={setOpenErrorModal}
        />
      )}
      {rexDisconnectedModal}
    </ContractSubPage>
  )
}

export default SaleInformation
